import youtubeAuth from './youtubeAuth';

const youtubeAPI = {
  /**
   * Get user's YouTube channel information
   */
  async getChannelInfo() {
    const accessToken = await youtubeAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with YouTube');
    }

    const response = await fetch(
      'https://www.googleapis.com/youtube/v3/channels?part=snippet,contentDetails&mine=true',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );

    const data = await response.json();

    if (data.error) {
      console.error('YouTube API error:', data.error);
      throw new Error(data.error.message || 'Failed to get YouTube channel info');
    }

    if (!data.items || data.items.length === 0) {
      throw new Error('No YouTube channel found for this account');
    }

    return data.items[0];
  },

  /**
   * Upload video to YouTube using resumable upload API
   * This is a multi-step process:
   * 1. Initialize resumable upload session
   * 2. Upload video in chunks
   * 3. Poll for processing status
   * 
   * @param {File} videoFile - The video file to upload
   * @param {string} title - Video title
   * @param {string} description - Video description
   * @param {Object} options - Additional options (privacyStatus, tags, categoryId)
   */
  async uploadVideo(videoFile, title, description, options = {}) {
    const accessToken = await youtubeAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with YouTube');
    }

    const {
      privacyStatus = 'public', // 'public', 'private', 'unlisted'
      tags = [],
      categoryId = '22' // People & Blogs (default for short videos)
    } = options;

    // Video metadata
    const metadata = {
      snippet: {
        title,
        description,
        tags,
        categoryId
      },
      status: {
        privacyStatus,
        selfDeclaredMadeForKids: false
      }
    };

    try {
      // Step 1: Initialize resumable upload session
      const initResponse = await fetch(
        'https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json; charset=UTF-8',
            'X-Upload-Content-Length': videoFile.size.toString(),
            'X-Upload-Content-Type': videoFile.type
          },
          body: JSON.stringify(metadata)
        }
      );

      if (!initResponse.ok) {
        const errorData = await initResponse.json();
        console.error('YouTube upload init error:', errorData);
        throw new Error(errorData.error?.message || 'Failed to initialize upload');
      }

      // Get the resumable upload URL from the response headers
      const uploadUrl = initResponse.headers.get('Location');
      if (!uploadUrl) {
        throw new Error('No upload URL received from YouTube');
      }

      // Step 2: Upload the video file
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': videoFile.type,
          'Content-Length': videoFile.size.toString()
        },
        body: videoFile
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('YouTube upload error:', errorText);
        throw new Error('Failed to upload video to YouTube');
      }

      const uploadData = await uploadResponse.json();
      console.log('YouTube upload response:', uploadData);

      return {
        success: true,
        videoId: uploadData.id,
        videoUrl: `https://www.youtube.com/watch?v=${uploadData.id}`,
        status: uploadData.status
      };
    } catch (error) {
      console.error('YouTube upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Upload video via backend (for larger files or when CORS is an issue)
   * The backend handles the actual upload to YouTube
   */
  async uploadVideoViaBackend(videoFile, title, description, options = {}) {
    const accessToken = await youtubeAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with YouTube');
    }

    const backendUrl = 'https://toldfel.hu';

    try {
      // First upload the video to our backend
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('title', title);
      formData.append('description', description);
      formData.append('access_token', accessToken);
      formData.append('privacy_status', options.privacyStatus || 'public');
      if (options.tags) {
        formData.append('tags', JSON.stringify(options.tags));
      }

      const response = await fetch(`${backendUrl}/api/youtube/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      return {
        success: true,
        videoId: data.video_id,
        videoUrl: `https://www.youtube.com/watch?v=${data.video_id}`,
        status: data.status
      };
    } catch (error) {
      console.error('YouTube backend upload error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Check video processing status
   */
  async getVideoStatus(videoId) {
    const accessToken = await youtubeAuth.getValidAccessToken();
    if (!accessToken) {
      throw new Error('Not authenticated with YouTube');
    }

    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=status,processingDetails&id=${videoId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
    );

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error.message || 'Failed to get video status');
    }

    if (!data.items || data.items.length === 0) {
      throw new Error('Video not found');
    }

    return data.items[0];
  }
};

export default youtubeAPI;
